export interface OpeningLine {
  slug: string;
  eco: string;
  name: string;
  family: string;
  color: "white" | "black";
  moves: string[];
}

export interface CourseCatalog {
  generatedAt: string;
  source: string;
  license: string;
  families: string[];
  openings: OpeningLine[];
}

export interface OpeningMatch {
  line: OpeningLine;
  matched: number;
}

export const COURSE_URL = `${import.meta.env.BASE_URL}courses/openings.json`;

export async function loadCourseCatalog(): Promise<CourseCatalog> {
  const response = await fetch(COURSE_URL);
  if (!response.ok) throw new Error("Could not load the opening catalogue.");
  return (await response.json()) as CourseCatalog;
}

function normalizeSan(san: string): string {
  return san
    .replace(/[!?+#]+/g, "")
    .replace(/\s+/g, "")
    .replace(/0-0/g, "O-O")
    .replace(/0-0-0/g, "O-O-O")
    .trim();
}

export function matchOpening(catalog: CourseCatalog, sans: string[]): OpeningMatch | null {
  let best: OpeningMatch | null = null;
  for (const line of catalog.openings) {
    const limit = Math.min(line.moves.length, sans.length);
    let matched = 0;
    while (matched < limit && normalizeSan(line.moves[matched] ?? "") === normalizeSan(sans[matched] ?? "")) {
      matched += 1;
    }
    if (matched === 0) continue;
    if (!best) {
      best = { line, matched };
      continue;
    }
    if (matched > best.matched) {
      best = { line, matched };
      continue;
    }
    if (matched === best.matched && line.moves.length < best.line.moves.length) {
      best = { line, matched };
    }
  }
  return best;
}

export interface ExplorerNode {
  key: string;
  san: string;
  ply: number;
  moveNumber: number;
  color: "white" | "black";
  children: ExplorerNode[];
  lines: OpeningLine[];
}

export function buildExplorerTree(catalog: CourseCatalog): ExplorerNode[] {
  const roots: ExplorerNode[] = [];

  for (const line of catalog.openings) {
    let level = roots;
    let node: ExplorerNode | null = null;
    let parentKey = "";

    for (const [index, san] of line.moves.entries()) {
      const ply = index + 1;
      const color: "white" | "black" = index % 2 === 0 ? "white" : "black";
      const moveNumber = Math.floor(index / 2) + 1;
      const key = level === roots ? `${ply}:${normalizeSan(san)}` : `${parentKey}/${ply}:${normalizeSan(san)}`;
      let found = level.find((child) => child.key === key);
      if (!found) {
        found = { key, san, ply, moveNumber, color, children: [], lines: [] };
        level.push(found);
      }
      node = found;
      parentKey = found.key;
      level = found.children;
    }

    if (node) node.lines.push(line);
  }

  return roots;
}

export function explorerLineName(node: ExplorerNode): string {
  return node.lines[0]?.name ?? node.lines[0]?.family ?? "";
}
